/**
 * RoutePrefetcher - Prefetches route chunks for faster navigation
 * Uses dynamic import() to actually prefetch JS chunks (SPA route prefetch)
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';

// Map paths to their chunk loader - triggers Vite to prefetch the actual JS bundle
const CHUNK_LOADERS: Record<string, () => Promise<unknown>> = {
  '/auth': () => import('@/pages/Auth'),
  '/buyer/dashboard': () => import('@/pages/buyer/Dashboard'),
  '/buyer/rfqs': () => import('@/pages/RFQManagement'),
  '/buyer/rfq/new': () => import('@/pages/buyer/CreateRFQ'),
  '/buyer/orders': () => import('@/pages/buyer/BuyerOrders'),
  '/buyer/quotes': () => import('@/pages/buyer/Quotes'),
  '/buyer/suppliers': () => import('@/pages/buyer/Suppliers'),
  '/buyer/messages': () => import('@/pages/Messages'),
  '/supplier/dashboard': () => import('@/pages/supplier/SupplierDashboard'),
  '/supplier/rfq-marketplace': () => import('@/pages/supplier/RFQMarketplace'),
  '/supplier/orders': () => import('@/pages/supplier/SupplierOrders'),
  '/supplier/quotes': () => import('@/pages/supplier/SupplierQuotes'),
  '/supplier/messages': () => import('@/pages/Messages'),
  '/admin/dashboard': () => import('@/pages/admin/AdminDashboard'),
  '/admin/users': () => import('@/pages/admin/AdminUsers'),
};

const prefetched = new Set<string>();

function prefetchChunk(path: string) {
  const normalized = path.replace(/\/$/, '') || '/';
  if (prefetched.has(normalized)) return;

  const loader = CHUNK_LOADERS[normalized];
  if (!loader) return;

  prefetched.add(normalized);
  loader().catch(() => {
    prefetched.delete(normalized); // Allow retry on failure
  });
}

export function RoutePrefetcher() {
  const location = useLocation();
  const { user, roles } = useAuth();
  const queryClient = useQueryClient();
  const prefetchScheduled = useRef(false);

  useEffect(() => {
    if (prefetchScheduled.current) return;
    prefetchScheduled.current = true;

    const prefetchRoutes = () => {
      const isBuyer = roles.includes('buyer');
      const isSupplier = roles.includes('supplier');
      const isAdmin = roles.includes('admin');

      if (user) {
        if (isBuyer) {
          prefetchChunk('/buyer/dashboard');
          prefetchChunk('/buyer/rfqs');
          prefetchChunk('/buyer/orders');
          prefetchChunk('/buyer/quotes');
          prefetchChunk('/buyer/suppliers');
          prefetchChunk('/buyer/messages');
          // (removed queryFn-less prefetchQuery calls — they poisoned the cache
          // with "Missing queryFn" and forced buyer dashboard/RFQ views to zeros)
        }
        if (isSupplier) {
          prefetchChunk('/supplier/dashboard');
          prefetchChunk('/supplier/rfq-marketplace');
          prefetchChunk('/supplier/orders');
          prefetchChunk('/supplier/quotes');
          prefetchChunk('/supplier/messages');
          // NOTE: do not prefetchQuery without a queryFn — React Query throws
          // "Missing queryFn" and poisons the cache entry, leaving the real
          // query (e.g. supplier dashboard) permanently paused / showing zeros.
          // Chunk prefetching above is enough; data is fetched by the page itself.
        }
        if (isAdmin) {
          prefetchChunk('/admin/dashboard');
          prefetchChunk('/admin/users');
          // (removed queryFn-less prefetchQuery for ['admin-dashboard-stats'] — it
          // poisoned the cache with "Missing queryFn" and forced the dashboard to zeros)
        }
      } else {
        prefetchChunk('/auth');
      }
    };

    if ('requestIdleCallback' in window) {
      (window as Window).requestIdleCallback(prefetchRoutes, { timeout: 2000 });
    } else {
      setTimeout(prefetchRoutes, 2000);
    }
  }, [location.pathname, user, roles, queryClient]);

  // Prefetch on link hover for instant navigation
  useEffect(() => {
    const handleLinkHover = (e: MouseEvent) => {
      const link = (e.target as Element)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!link?.href) return;
      try {
        const url = new URL(link.href);
        if (url.origin === window.location.origin) {
          prefetchChunk(url.pathname);
        }
      } catch {
        // Invalid URL
      }
    };

    document.addEventListener('mouseenter', handleLinkHover, { passive: true, capture: true });
    return () => document.removeEventListener('mouseenter', handleLinkHover, { capture: true });
  }, []);

  return null;
}

